import { useState, useEffect, useRef } from "react";
import { Check, X, Pencil, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type EditableValue = string | number | boolean | null | undefined;

interface BaseProps {
  label: string;
  hint?: string;
  mono?: boolean;
  disabled?: boolean;
  onSave: (value: EditableValue) => Promise<void> | void;
}

interface TextProps extends BaseProps {
  type: "text" | "textarea";
  value: string | null | undefined;
}

interface NumberProps extends BaseProps {
  type: "number";
  value: number | null | undefined;
  step?: string;
}

interface BooleanProps extends BaseProps {
  type: "boolean";
  value: boolean | null | undefined;
}

type Props = TextProps | NumberProps | BooleanProps;

function isEmpty(v: EditableValue): boolean {
  return v === null || v === undefined || v === "";
}

function formatValue(v: EditableValue, type: Props["type"]): string {
  if (type === "boolean") return v ? "Ja" : "Nein";
  if (isEmpty(v)) return "";
  return String(v);
}

export function EditableField(props: Props) {
  const { label, hint, mono, disabled, onSave, type, value } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() => {
    if (type === "boolean") return value ? "true" : "false";
    return value === null || value === undefined ? "" : String(value);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if ("select" in inputRef.current) inputRef.current.select();
    }
  }, [editing]);

  function startEdit() {
    if (disabled || saving) return;
    if (type === "boolean") {
      setDraft(value ? "true" : "false");
    } else {
      setDraft(value === null || value === undefined ? "" : String(value));
    }
    setError(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError(null);
  }

  async function commit() {
    setSaving(true);
    setError(null);
    let parsed: EditableValue;
    if (type === "boolean") {
      parsed = draft === "true";
    } else if (type === "number") {
      if (draft === "") {
        parsed = null;
      } else {
        const n = Number(draft);
        if (Number.isNaN(n)) {
          setError("Keine gültige Zahl");
          setSaving(false);
          return;
        }
        parsed = n;
      }
    } else {
      parsed = draft === "" ? null : draft;
    }
    try {
      await onSave(parsed);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") cancel();
    if (e.key === "Enter" && type !== "textarea") {
      e.preventDefault();
      void commit();
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && type === "textarea") {
      e.preventDefault();
      void commit();
    }
  }

  return (
    <div className="flex flex-col gap-1 group">
      <div className="text-[10px] uppercase tracking-wider text-muted font-semibold">
        {label}
      </div>
      {editing ? (
        <div className="flex items-start gap-1.5">
          {type === "boolean" ? (
            <select
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className="flex-1 px-2 py-1 text-sm rounded border border-primary bg-background outline-none"
            >
              <option value="true">Ja</option>
              <option value="false">Nein</option>
            </select>
          ) : type === "textarea" ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              rows={4}
              className={cn(
                "flex-1 px-2 py-1 text-sm rounded border border-primary bg-background outline-none resize-y",
                mono && "font-mono",
              )}
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={type === "number" ? "number" : "text"}
              step={type === "number" ? (props as NumberProps).step : undefined}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              className={cn(
                "flex-1 px-2 py-1 text-sm rounded border border-primary bg-background outline-none",
                mono && "font-mono",
              )}
            />
          )}
          <button
            onClick={() => void commit()}
            disabled={saving}
            className="inline-flex items-center justify-center size-7 rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
            title="Speichern"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center justify-center size-7 rounded border border-line hover:bg-panel-2 disabled:opacity-50"
            title="Abbrechen"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={startEdit}
          disabled={disabled}
          className={cn(
            "text-left text-sm px-2 py-1 -mx-2 -my-1 rounded border border-transparent",
            !disabled && "hover:bg-panel-2 hover:border-line cursor-pointer",
            disabled && "cursor-default",
            mono && "font-mono",
            isEmpty(value) && "italic text-muted-2",
          )}
        >
          <span className="inline-flex items-center gap-2">
            {isEmpty(value) ? "leer" : formatValue(value, type)}
            {!disabled && (
              <Pencil className="size-3 text-muted opacity-0 group-hover:opacity-100" />
            )}
          </span>
        </button>
      )}
      {error && <div className="text-[11px] text-coral">{error}</div>}
      {hint && <div className="text-[11px] text-muted">{hint}</div>}
    </div>
  );
}
